import { useState } from 'react';
import {
  makeStyles, tokens, Title2, Body1, Caption1, Spinner, MessageBar, MessageBarBody, Badge,
  Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableCell,
} from '@fluentui/react-components';
import { ProjectPicker } from '../../../components/ProjectPicker';
import { useCostVsEstimate } from '../../../api/hooks';
import type { CostVsEstimateRow } from '../../../api/types';

// FEAT-022 Cost-vs-Estimate. Margin tracker per project. Server side audit-logs every
// access (Read.CostVsEstimate / Project / projectId) — required by AC007.
const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, height: '100%' },
  hint: { color: tokens.colorNeutralForeground2 },
  toolbar: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  stat: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statValue: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold },
  statLabel: { color: tokens.colorNeutralForeground2 },
  tableWrapper: { flex: 1, overflow: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  num: { fontFamily: tokens.fontFamilyMonospace, textAlign: 'right' },
});

function ragColor(rag: CostVsEstimateRow['ragState']) {
  return rag === 'Green' ? 'success' as const : rag === 'Amber' ? 'warning' as const : 'danger' as const;
}

function aud(n: number): string {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n);
}

export function CostVsEstimatePage() {
  const styles = useStyles();
  const [projectId, setProjectId] = useState<string | undefined>();
  const { data, isLoading, error } = useCostVsEstimate(projectId);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Title2>Cost vs Estimate</Title2>
        <Body1 className={styles.hint}>
          Approved estimate vs delivered cost per Epic / Feature. Audit-logged on every access.
          Loaded rate is configurable in <strong>Admin → Central Config</strong>.
        </Body1>
        <ProjectPicker selectedProjectId={projectId} onSelect={setProjectId} />
      </div>

      {!projectId ? (
        <Body1>Pick a project.</Body1>
      ) : isLoading ? (
        <Spinner label="Computing margin…" />
      ) : error ? (
        <MessageBar intent="error"><MessageBarBody>{(error as Error).message}</MessageBarBody></MessageBar>
      ) : !data || data.rows.length === 0 ? (
        <Body1>No Epics or Features in this project carry estimate / completed-work hours.</Body1>
      ) : (
        <>
          <div className={styles.summary}>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Loaded rate</Caption1>
              <span className={styles.statValue}>{aud(data.loadedRatePerHourAud)} <Caption1>/ hr ({data.loadedRateRole})</Caption1></span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Total estimate</Caption1>
              <span className={styles.statValue}>{aud(data.totalOriginalEstimateAud)}</span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Delivered</Caption1>
              <span className={styles.statValue}>{aud(data.totalDeliveredCostAud)}</span>
            </div>
            <div className={styles.stat}>
              <Caption1 className={styles.statLabel}>Variance</Caption1>
              <span className={styles.statValue}>{data.totalVariancePercent.toFixed(1)}%</span>
            </div>
          </div>

          {data.byRole.length > 0 && (
            <>
              <Caption1 className={styles.statLabel}>Delivered by role (hours × loaded rate)</Caption1>
              <div className={styles.tableWrapper}>
                <Table aria-label="Delivered cost by assignee role" size="small">
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Role</TableHeaderCell>
                      <TableHeaderCell>Hours</TableHeaderCell>
                      <TableHeaderCell>Delivered ($)</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.byRole.map((r) => (
                      <TableRow key={r.role}>
                        <TableCell>{r.role}</TableCell>
                        <TableCell className={styles.num}>{r.hours.toFixed(0)}</TableCell>
                        <TableCell className={styles.num}>{aud(r.costAud)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}

          <div className={styles.tableWrapper}>
            <Table aria-label="Per-work-item cost vs estimate" size="small">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>ID</TableHeaderCell>
                  <TableHeaderCell>Title</TableHeaderCell>
                  <TableHeaderCell>Assignee</TableHeaderCell>
                  <TableHeaderCell>Role / Rate</TableHeaderCell>
                  <TableHeaderCell>Estimate (hrs)</TableHeaderCell>
                  <TableHeaderCell>Done (hrs)</TableHeaderCell>
                  <TableHeaderCell>Estimate ($)</TableHeaderCell>
                  <TableHeaderCell>Delivered ($)</TableHeaderCell>
                  <TableHeaderCell>Variance</TableHeaderCell>
                  <TableHeaderCell>RAG</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell><Caption1>#{r.id}</Caption1></TableCell>
                    <TableCell>{r.title}</TableCell>
                    <TableCell><Caption1>{r.assigneeDisplayName ?? '—'}</Caption1></TableCell>
                    <TableCell><Caption1>{r.assigneeRole} · {aud(r.assigneeRatePerHourAud)}/hr</Caption1></TableCell>
                    <TableCell className={styles.num}>{r.originalEstimateHours.toFixed(0)}</TableCell>
                    <TableCell className={styles.num}>{r.completedWorkHours.toFixed(0)}</TableCell>
                    <TableCell className={styles.num}>{aud(r.estimatedCostAud)}</TableCell>
                    <TableCell className={styles.num}>{aud(r.deliveredCostAud)}</TableCell>
                    <TableCell className={styles.num}>{r.variancePercent.toFixed(1)}%</TableCell>
                    <TableCell><Badge appearance="filled" color={ragColor(r.ragState)}>{r.ragState}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
